<template>
  <div>
    <page-header
      :page="headerPage"
      :actions="headerActions"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />
    <board class="mb-3" :details="boardDetails">
      <template #data-item="{ item }">
        <div v-if="!item.data">
          <custom-balloon
            :background-color="item.statusColor"
            :text="item.statusName"
            dot
          />
        </div>
      </template>
    </board>
    <div class="mb-4">
      <div class="row">
        <div class="col-12">
          <q-tab :active-tab="activeTab">
            <q-tab-item
              v-if="!cameFromOrderBooks"
              :tabindex="0"
              title="Información del Libro"
              @click="setCurrentTab(0)"
            >
              <loan-information :loan="loanInfo" />
            </q-tab-item>
            <q-tab-item
              v-if="!cameFromOrderBooks"
              :tabindex="1"
              title="Historial de Libro"
              lazy
              @click="setCurrentTab(1)"
            >
              <book-history :book="details" />
            </q-tab-item>
            <q-tab-item
              v-if="!cameFromOrderBooks"
              :tabindex="2"
              title="Historial de Bitácora"
              @click="setCurrentTab(2)"
            >
              <binnacle-history :history="binnacles" />
            </q-tab-item>
            <q-tab-item
              :tabindex="3"
              title="Historial de Custodio"
              @click="setCurrentTab(3)"
              :navItemClass="cameFromOrderBooks ? 'custom-width' : ''"
            >
              <custodian-history />
            </q-tab-item>
          </q-tab>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import PageHeader from "PageHeader";
import Board from "Board";
import { QTab, QTabItem } from "@/components/common/q-tabs";
import alertMixin from "@/mixin/alert.mixin";
import service from "@/services/api/inventorycatalog.api";
import CustomBalloon from "custom-balloon";
import { name as appModuleName } from "@/modules/app/store/index";
import { mapGetters } from "vuex";
import {
  EquipmentInformation,
  BookHistory,
  CustodianHistory,
  BinnacleHistory
} from "./tabs";

import { LoanInformation } from "./components";

const TABS = Object.freeze({
  Information: 0,
  History: 1,
  Binnacle: 2,
  Custodian: 3
});

export default {
  name: "InventoryBookDetails",
  components: {
    PageHeader,
    Board,
    QTab,
    QTabItem,
    CustomBalloon,
    EquipmentInformation,
    BookHistory,
    CustodianHistory,
    BinnacleHistory,
    LoanInformation
  },
  mixins: [alertMixin],
  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (
        from.name === "inventoryOrderBooks" ||
        from.name === "requestsBooks"
      ) {
        vm.cameFromOrderBooks = true;
      }
    });
  },
  data: () => ({
    details: {},
    loanInfo: {},
    binnacles: [],
    activeTab: TABS.Information,
    cameFromOrderBooks: false
  }),
  computed: {
    ...mapGetters(appModuleName, {
      isMobile: "getIsMobileDensity"
    }),
    catalogId() {
      return Number(this.$route.params.id);
    },
    bookId() {
      return Number(this.$route.params.bookId);
    },
    headerPage() {
      return {
        icon: "fas fa-book-open",
        title: `Administración Inventario: ${this.details.catalogName}`,
        routes: ["Inicio", "Libros", "Catálogo de Libros"]
      };
    },
    headerActions() {
      return [
        {
          text: "Regresar",
          icon: "fas fa-arrow-circle-left",
          variant: "outline-success",
          click: () => this.$router.go(-1)
        }
      ];
    },
    boardDetails() {
      return [
        [
          {
            label: "Título de Libro:",
            data: this.details.title ?? "-",
            cols: "col-12 col-md pt-2"
          },
          {
            label: "Código de Libro:",
            data: this.details.code ?? "-",
            cols: "col-12 col-md pt-2"
          },
          {
            label: "Grado:",
            data: this.details.grade ?? "-",
            cols: "col-12 col-md pt-2"
          },
          {
            label: "Centro:",
            data: this.details.caat ?? "-",
            cols: "col-12 col-md pt-2"
          },
          {
            label: "Estatus:",
            statusColor: this.details.statusColor,
            statusName: this.details.statusName,
            cols: "col-12 col-md pt-2"
          }
        ]
      ];
    },
    tabParam() {
      return Number(this.$route.query.tab ?? TABS.Information);
    }
  },
  async mounted() {
    await this.initData();
  },
  methods: {
    async initData() {
      await this.getBookBinnacles();
      await this.getEquipmentLoanDetails();
      await this.getBookDetails();
      if (this.tabParam) {
        this.activeTab = this.tabParam;
      }
    },
    async getBookDetails() {
      const { data } = await service.getBook(this.bookId);
      this.details = data;
    },
    async getEquipmentLoanDetails() {
      const { data } = await service.getLoanInformation(this.bookId);
      this.loanInfo = data;
    },
    async getBookBinnacles() {
      const { data } = await service.getBookBinnaclesHistory(this.bookId);
      this.binnacles = data;
    },
    setCurrentTab(tab) {
      const sameRoute = this.tabParam === tab;
      if (sameRoute) return;

      this.activeTab = tab;
      if (tab) this.$router.replace({ query: { tab } });
      else this.$router.replace({ name: this.$route.name });
    }
  }
};
</script>
<style scoped lang="scss">
::v-deep .font-weight-bold {
  font-weight: 700 !important;
}
::v-deep .custom-tab .custom-qtap > div:first-child .nav-item.custom-width {
  @media only screen and (min-width: 768px) {
    flex: none !important;
    width: 25%;
  }
}
</style>
